/**
 * SelectBox Component.
 * Its Wrapper Component for `react-datepicker` Component.
 */

import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { FormField, Glyph } from 'kepler';
import DatePicker from 'react-datepicker';
import moment from 'moment';
import * as shape from '../../constants/PropTypesShapes';

class DateSelector extends Component {

  // Set the Field Settings and Error as States.
  constructor(props) {
    super(props);

    const { options } = props;

    this.state = {
      options,
      errors: [],
    };
  }

  // Every Component Re-render with New Props.
  // This will update the Field Settings and validate according to the prop change during Topic Tree Selection.
  componentWillReceiveProps(props) {
    const { validate, options } = props;

    this.setState({
      options,
    });

    if (validate === true) {
      this.validate(options.value);
    }
  }

  // Send the Field State to Form.
  setFormState() {
    this.props.handleValue(this.state.options);
  }

  // Convert the Date to Moment Object.
  toMoment(value) {
    if (!this.isValid(value)) {
      return null;
    }
    return moment(value, this.props.format);
  }

  // Convert the Moment Date Object to Default Date String.
  toDate(dateObject) {
    return (dateObject && dateObject.format) ? dateObject.format(this.props.format) : '';
  }

  // Check, If Field Value is empty.
  isEmpty(value) {
    return (this.props.options.isRequired && value === '');
  }

  // Check, If Field Date Value is Valid.
  isValid(value) {
    return (moment(value, this.props.format).isValid());
  }

  // Validate Field Value and Update the State.
  validate(value) {
    const errors = [];
    const now = moment();
    const options = this.props.options;
    const isInValid = (!this.isValid(value));
    const isFutureDate = (!moment(value).isSame(now)) && moment(value).isAfter(now);

    if (this.isEmpty(value)) {
      errors.push(this.props.errorWarn.required);
      options.hasError = true;
      options.value = '';
    } else if (isInValid || isFutureDate) {
      errors.push(this.props.errorWarn.invalidDate);
      options.hasError = true;
      options.value = value;
    } else {
      options.hasError = false;
      options.value = value;
    }

    this.setState({
      options,
      errors,
    });
  }

  // Return Error Icon according to the State Errors Object.
  errorIcon() {
    let errorIcon = null;
    const hasError = (this.state.errors) ? this.state.errors.length : 0;

    if (hasError) {
      errorIcon = (
        <Glyph size="small" type="error" icon="icon-critical-small" className="Input-error-icon" />
      );
    }

    return errorIcon;
  }

  // Return Error Block Element according to the State Errors Object.
  errors() {
    let errorBlock = null;
    const hasError = (this.state.errors) ? this.state.errors.length : 0;

    if (hasError) {
      errorBlock = (
        <div className="Input-error">
          <ul>
            {this.state.errors.map(error => (
              <li>{error}</li>
            ))}
          </ul>
        </div>
      );
    }

    return errorBlock;
  }

  // Handle during Blur Event.
  // It is similar to `handleChange` Method.
  // Convert the value to `moment` object and send it to `validate` and `form`.
  handleBlur(value) {
    this.validate(value);
    this.setFormState();
  }

  // Handle during Change Event.
  // Convert the value to `moment` object and send it to `validate` and `form`.
  handleChange(value) {
    this.validate(this.toDate(value));
    this.setFormState();
  }

  // Render the `DateSelector` Component.
  render() {
    const fieldClassName = (this.state.errors && this.state.errors.length > 0) ? 'Input error' : 'Input';
    return (
      <FormField>
        <label htmlFor={this.props.options.id} className="formLabel">
          {this.props.options.id}
        </label>
        <div className="Input-parent date-selector">
          <DatePicker
            id={this.props.options.id}
            className={fieldClassName}
            dateFormat={this.props.format}
            maxDate={moment()}
            selected={this.toMoment(this.state.options.value)}
            onChange={date => this.handleChange(date)}
            onBlur={e => this.handleBlur(e.target.value)}
            placeholderText={this.props.options.label}
          />
          {this.errorIcon()}
          {this.errors()}
        </div>
      </FormField>
    );
  }
}

// FormButtons Props Types.
DateSelector.propTypes = {
  // Simple Props.
  validate: PropTypes.bool, // eslint-disable-line react/no-unused-prop-types
  format: PropTypes.string,

  // Complex Props.
  options: PropTypes.shape({
    id: PropTypes.string,
    type: PropTypes.string,
    label: PropTypes.string,
    name: PropTypes.string,
    isRequired: PropTypes.bool,
    value: PropTypes.string,
    hasError: PropTypes.bool,
  }),

  // Method Props.
  handleValue: PropTypes.func.isRequired,
  errorWarn: PropTypes.shape(shape.WEBFORM_ERRORS),
};

// EmailUs Props default values.
DateSelector.defaultProps = {
  validate: false,
  format: 'MM/DD/YYYY',
  options: {},
  errorWarn: {},
};

export default DateSelector;



// WEBPACK FOOTER //
// ./contactUs/components/form/DateSelector.js